/**
 * FieldIssueTab (M2 · updated per spec) — MIR lifecycle Kanban.
 *
 * Flow: Site Engineer creates MIR (WBS + workfront + MCI + qty + required-by,
 * validated against live IQC-OK Stock) → Storekeeper approves + reserves
 * (FIFO by GRN date) → Issue Counter scan (#4) → auto Gate Pass →
 * WH Exit (#5) → Site Entry (#6) → POU (#7) → Install (#8).
 *
 * UI blocks:
 *  1. 6 KPI tiles
 *  2. Toolbar: New MIR · Bootstrap MTX
 *  3. 9-lane Kanban (DRAFTED → INSTALLED) — cards link to MCI/GRN/PO
 *  4. Gate Pass Register table
 *  5. Interface Point Status widget
 *  6. Right-sticky Cognara panel
 *
 * Rules enforced:
 *  - Only IQC-Accepted stock reservable (backend validates)
 *  - FIFO by GRN date on approve (backend sorts by iqcAcceptedAt)
 *  - Reserved stock can't double-issue (unit currentState machine)
 *  - Site Entry within 4h of WH Exit (backend TTL)
 *  - POU within 8h of Site Entry (backend TTL)
 *  - Install blocked until POU scan exists (backend TRANSITIONS)
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import MciPackageDrawer from '@/components/procurement/MciPackageDrawer';
import ScanCenterDialog from '@/components/mtx/ScanCenterDialog';
import GatePassLabel from '@/components/mtx/GatePassLabel';
import { Plus, Loader2, ClipboardList, X, Check, ChevronRight, Sparkles,
         Ticket, Radio, ExternalLink, AlertTriangle, RefreshCw, ScanLine, Printer,
         FileSpreadsheet, Users, Briefcase, ShieldCheck, Package as PkgIcon,
         MapPin, ArrowRight, XCircle, CheckCircle2, HelpCircle } from 'lucide-react';

const C = { primary: '#1E4E8C', accent: '#F26B1F', slate: '#0F172A',
             slateMuted: '#64748B', border: 'rgba(15,23,42,0.10)',
             success: '#10B981', warn: '#F59E0B', danger: '#EF4444', info: '#3B82F6' };

const BUCKETS = [
  { key: 'DRAFTED',        label: 'Drafted',         color: '#94A3B8' },
  { key: 'AWAITING_STOCK', label: 'Awaiting Stock',  color: '#EAB308' },
  { key: 'SUBMITTED',      label: 'Submitted',       color: '#F59E0B' },
  { key: 'APPROVED',       label: 'Approved',        color: '#3B82F6' },
  { key: 'ISSUED',         label: 'Issued',          color: '#8B5CF6' },
  { key: 'AT_WH_GATE',     label: 'At WH Gate',      color: '#F26B1F' },
  { key: 'IN_TRANSIT',     label: 'In Transit',      color: '#0EA5E9' },
  { key: 'AT_SITE_GATE',   label: 'At Site Gate',    color: '#F97316' },
  { key: 'RECEIVED',       label: 'Received @ POU',  color: '#22C55E' },
  { key: 'INSTALLED',      label: 'Installed',       color: '#059669' },
];

// ─── 4-tier workflow personas (Site Engineer → Package Mgr → Project Mgr → Store) ─
const PERSONAS = [
  {
    id:    'SITE_ENGINEER',
    label: 'Site Engineer',
    icon:  Users,
    color: '#3B82F6',
    help:  'Raise a new MIR from your workfront. Attach WBS activity, quantity and required-by date. The request auto-routes to the Package Manager for approval.',
    actionLabel: 'Raise MIR',
    stageColumns: ['DRAFTED', 'AWAITING_STOCK', 'SUBMITTED'],
  },
  {
    id:    'PACKAGE_MANAGER',
    label: 'Package Manager',
    icon:  Briefcase,
    color: '#8B5CF6',
    help:  'Review MIRs raised within your work-package. Approve if the scope and quantity match the plan — the request then flows to the Project Manager.',
    actionLabel: 'Approve as Package Mgr',
    stageColumns: ['SUBMITTED', 'AWAITING_STOCK'],
  },
  {
    id:    'PROJECT_MANAGER',
    label: 'Project Manager',
    icon:  ShieldCheck,
    color: '#F59E0B',
    help:  'Final sign-off before Stores can release material. Verify budget, priority and safety pre-requisites. Approving hands the request to the Store Incharge for dispatch.',
    actionLabel: 'Approve as Project Mgr',
    stageColumns: ['SUBMITTED', 'AWAITING_STOCK'],
  },
  {
    id:    'STORE_INCHARGE',
    label: 'Store Incharge',
    icon:  PkgIcon,
    color: '#10B981',
    help:  'Dispatch approved MIRs from the warehouse. Approval reserves stock FIFO by GRN, prints the Gate Pass, and updates the layout/inventory automatically.',
    actionLabel: 'Dispatch from Store',
    stageColumns: ['SUBMITTED', 'AWAITING_STOCK', 'APPROVED', 'AT_WH_GATE'],
  },
];

// GPS capture helper — silent, non-blocking, 6-second timeout.
async function captureGps(timeoutMs = 6000) {
  if (!('geolocation' in navigator)) return null;
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(null), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(t);
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          capturedAt: new Date().toISOString(),
        });
      },
      () => { clearTimeout(t); resolve(null); },
      { enableHighAccuracy: true, timeout: timeoutMs - 500, maximumAge: 30000 });
  });
}

function CreateMirDialog({ projectId, onClose, onCreated, prefill }) {
  const [warehouses, setWarehouses] = useState([]);
  const [mciResults, setMciResults] = useState([]);
  const [wbsList, setWbsList] = useState([]);
  const [scheduleCode, setScheduleCode] = useState('');
  const [scheduleName, setScheduleName] = useState('');
  const [availableSchedules, setAvailableSchedules] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const [wbsDropdownOpen, setWbsDropdownOpen] = useState(false);
  const [warehouseLocation, setWarehouseLocation] = useState('');
  const [wbs, setWbs] = useState('');
  const [wbsSearch, setWbsSearch] = useState('');
  const [workfront, setWorkfront] = useState(prefill?.workfront || '');
  const [reqBy, setReqBy] = useState('');
  const [mci, setMci] = useState(prefill?.materialCode || '');
  const [mciDisplay, setMciDisplay] = useState(prefill?.materialCode
    ? `${prefill.materialCode}${prefill.description ? ' · ' + prefill.description : ''}`
    : '');
  const [mciSearch, setMciSearch] = useState('');
  const [mciMax, setMciMax] = useState(null);
  const [qty, setQty] = useState(prefill?.qty ? String(prefill.qty) : '');
  const [notes, setNotes] = useState(prefill?.mark
    ? `Auto-filled from SWM scan · Mark ${prefill.mark}`
    : '');
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // Current user (auto-captured from JWT-stored profile)
  const user = (() => {
    try { return JSON.parse(localStorage.getItem('ciq_user') || '{}'); }
    catch { return {}; }
  })();

  // Load warehouses + WBS activities on mount
  useEffect(() => {
    (async () => {
      try {
        const [{ data: wh }, wbsResp, mtxWbs] = await Promise.all([
          api.get(`/v1/projects/${projectId}/mtx/warehouses`),
          api.get(`/v1/projects/${projectId}/scheduling/tasks?limit=1000`).catch(() => ({ data: { tasks: [] } })),
          api.get(`/v1/projects/${projectId}/mtx/wbs-schedule${selectedScheduleId ? '?schedule_id=' + encodeURIComponent(selectedScheduleId) : ''}`).catch(() => ({ data: {} })),
        ]);
        setWarehouses(wh.warehouses || []);
        // Prefer the MTX-scoped macro schedule (has SO-97.16 name); fall
        // back to raw scheduling tasks when unavailable.
        const mtxActs = mtxWbs?.data?.activities || [];
        const legacyTasks = (wbsResp.data.tasks || wbsResp.data || [])
          .filter(t => t.wbsCode || t.wbsUid || t.activityId || t.id);
        const combined = mtxActs.length ? mtxActs.map(a => ({
          wbsCode: a.code, name: a.name, phase: a.phase,
          workfront: a.workfront, depth: a.depth,
        })) : legacyTasks;
        setWbsList(combined.slice(0, 2000));
        setScheduleCode(mtxWbs?.data?.scheduleCode || '');
        setScheduleName(mtxWbs?.data?.scheduleName || '');
        setAvailableSchedules(mtxWbs?.data?.schedules || []);
        // Default warehouse to prefill match (SWM deep-link) or the
        // healthiest one when no prefill is provided.
        if ((wh.warehouses || []).length && !warehouseLocation) {
          const prefWh = prefill?.warehouse
            ? (wh.warehouses || []).find(w =>
                (w.location || '').toLowerCase().includes(String(prefill.warehouse).toLowerCase())
                || (w.name || '').toLowerCase().includes(String(prefill.warehouse).toLowerCase())
              )
            : null;
          const first = prefWh || wh.warehouses.find(w => w.iqcOkStock > 0) || wh.warehouses[0];
          setWarehouseLocation(first.location);
        }
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, selectedScheduleId]);

  // MCI search — debounced against material-analysis (in-warehouse) as
  // primary source, with mciEntries fallback if warehouse has no ledger yet.
  useEffect(() => {
    if (!warehouseLocation || mciSearch.length < 2) { setMciResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/v1/projects/${projectId}/mtx/warehouses/${encodeURIComponent(warehouseLocation)}/material-analysis`);
        const q = mciSearch.toLowerCase();
        setMciResults((data.materialCodes || []).filter(r =>
          (r.mciLineNumber || '').toLowerCase().includes(q)
          || (r.description || '').toLowerCase().includes(q)
          || (r.materialCode || '').toLowerCase().includes(q)
        ).slice(0, 12));
      } catch { setMciResults([]); }
    }, 280);
    return () => clearTimeout(t);
  }, [projectId, warehouseLocation, mciSearch]);

  // When MCI selected, use its Final Inventory as max available
  const pickMci = async (row) => {
    setMci(row.mciLineNumber);
    setMciDisplay(`${row.mciLineNumber} · ${row.description || row.materialCode || ''}`);
    setMciSearch('');
    setMciResults([]);
    setMciMax(row.finalInventory ?? row.iqcAccepted ?? null);
  };

  const submit = async () => {
    if (!warehouseLocation.trim() || !mci.trim() || !parseFloat(qty)) {
      toast.error('Warehouse, MCI and Qty are required'); return;
    }
    if (mciMax != null && parseFloat(qty) > mciMax) {
      if (!window.confirm(`Qty ${qty} exceeds available inventory (${mciMax}). Proceed anyway?`)) return;
    }
    setSaving(true);
    try {
      // Capture GPS for the audit trail — silent if the browser denies.
      const creatorGps = await captureGps().catch(() => null);
      const { data } = await api.post(`/v1/projects/${projectId}/mtx/mirs`, {
        warehouseLocation: warehouseLocation.trim(),
        wbsActivityId: wbs.trim() || null, workfront: workfront.trim() || null,
        requiredByDate: reqBy || null, notes: notes || null,
        creatorGps,
        lineItems: [{ mciLineNumber: mci.trim(), qty: parseFloat(qty) }],
      });
      if (data.status === 'AWAITING_STOCK' || data.awaitingStock) {
        toast.warning(`MIR ${data.code} raised · AWAITING STOCK (no IQC-Accepted units yet). Request will queue for the Package Manager.`);
      } else {
        toast.success(`MIR ${data.code} created → routed to Package Manager for approval`);
      }
      onCreated?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create MIR'); }
    setSaving(false);
  };

  const inp = "w-full px-2 py-1.5 rounded border text-xs";
  const lbl = "text-[10px] uppercase font-semibold mb-1 block";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}
      data-testid="mir-create-dialog">
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: C.border }}>
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" style={{ color: C.accent }} />
            <div>
              <div className="text-sm font-bold" style={{ color: C.slate }}>New Material Issue Request</div>
              <div className="text-[9.5px]" style={{ color: C.slateMuted }}>
                Raised by <b>{user.name || user.email || 'current user'}</b>
                {user.email && user.name ? <> ({user.email})</> : null} · {new Date().toLocaleString()}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setImportOpen(true)}
              data-testid="mir-bulk-open"
              className="text-[10px] px-2 py-1 rounded font-semibold flex items-center gap-1"
              style={{ background: '#EEF2FF', color: C.primary }}>
              <FileSpreadsheet className="w-3 h-3" /> Bulk Excel
            </button>
            <button onClick={onClose}><X className="w-4 h-4" style={{ color: C.slateMuted }} /></button>
          </div>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className={lbl} style={{ color: C.slateMuted }}>Warehouse *</label>
            <select value={warehouseLocation} onChange={e => { setWarehouseLocation(e.target.value); setMci(''); setMciDisplay(''); }}
              className={inp} style={{ borderColor: C.border }} data-testid="mir-warehouse">
              <option value="">— pick warehouse —</option>
              {warehouses.map(w => (
                <option key={w.id} value={w.location}>
                  {w.label || w.location} · {w.iqcOkStock || 0} IQC-OK
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2 relative">
            <label className={lbl} style={{ color: C.slateMuted }}>MCI Line * (search by code / description / material)</label>
            {mci && (
              <div className="p-1.5 rounded border mb-1 flex items-center justify-between"
                style={{ background: '#F0FDF4', borderColor: C.success }}
                data-testid="mir-mci-picked">
                <div className="text-[11px]"><b>{mci}</b> — {mciDisplay.split(' · ')[1] || ''}</div>
                <button onClick={() => { setMci(''); setMciDisplay(''); setMciMax(null); }}
                  className="text-[10px]" style={{ color: C.slateMuted }}>change</button>
              </div>
            )}
            {!mci && (
              <input value={mciSearch} onChange={e => setMciSearch(e.target.value)}
                placeholder="Type at least 2 chars — e.g. MCI-ADR, CS Pipe, Alfa"
                className={inp} style={{ borderColor: C.border }} data-testid="mir-mci-search"
                disabled={!warehouseLocation} />
            )}
            {mciResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded shadow-lg z-30 max-h-56 overflow-y-auto"
                style={{ borderColor: C.border }}>
                {mciResults.map((r, i) => (
                  <button key={i} onClick={() => pickMci(r)}
                    className="block w-full text-left px-2 py-1 text-[10px] hover:bg-slate-50"
                    style={{ borderBottom: `1px solid ${C.border}` }}
                    data-testid={`mir-mci-option-${i}`}>
                    <div className="font-mono font-bold" style={{ color: C.primary }}>{r.mciLineNumber}</div>
                    <div className="truncate" style={{ color: C.slate }}>{r.description || r.materialCode}</div>
                    <div style={{ color: C.slateMuted }}>
                      PO {r.poNumber} · {r.vendor} · Available <b style={{ color: C.success }}>{r.finalInventory ?? r.iqcAccepted}</b> {r.unit}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {mciMax != null && (
              <div className="text-[10px] mt-0.5" style={{ color: C.slateMuted }}>
                Available in this warehouse: <b style={{ color: C.success }}>{mciMax}</b>
              </div>
            )}
          </div>
          <div>
            <label className={lbl} style={{ color: C.slateMuted }}>Qty *</label>
            <input type="number" value={qty} onChange={e => setQty(e.target.value)}
              placeholder={mciMax != null ? `Max ${mciMax}` : '1'}
              className={inp} style={{ borderColor: C.border, color: mciMax != null && parseFloat(qty) > mciMax ? C.danger : C.slate }}
              data-testid="mir-qty" />
          </div>
          <div>
            <label className={lbl} style={{ color: C.slateMuted }}>Required By</label>
            <input type="date" value={reqBy} onChange={e => setReqBy(e.target.value)}
              className={inp} style={{ borderColor: C.border }} data-testid="mir-required-by" />
          </div>
          <div className="relative">
            <label className={lbl} style={{ color: C.slateMuted }}>
              WBS Activity (macro schedule · searchable)
              {scheduleCode && (
                <span className="ml-1 text-[9px] font-bold" style={{ color: C.primary }}>
                  · {scheduleCode}
                </span>
              )}
              {scheduleName && (
                <span className="ml-1 text-[8.5px] font-normal" style={{ color: C.slateMuted }}>
                  ({scheduleName})
                </span>
              )}
            </label>
            {availableSchedules.length > 1 && (
              <select value={selectedScheduleId}
                onChange={e => { setSelectedScheduleId(e.target.value); setWbs(''); }}
                className={inp}
                style={{ borderColor: C.primary, marginBottom: 4 }}
                data-testid="mir-schedule-selector">
                <option value="">— Auto-select (first available) —</option>
                {availableSchedules.map(s => (
                  <option key={s.id} value={s.id}>{s.name || s.code || s.id}</option>
                ))}
              </select>
            )}
            {wbs ? (
              <div className="p-1.5 rounded border flex items-center justify-between"
                style={{ background: '#F0FDF4', borderColor: C.success }}>
                <div className="text-[10.5px]">
                  <b className="font-mono">{wbs}</b>
                  <div style={{ color: C.slateMuted, fontSize: 9 }}>
                    {(() => {
                      const t = wbsList.find(t => (t.wbsCode || t.activityId || t.id) === wbs);
                      return t ? (t.name || t.taskName || '') : '';
                    })()}
                  </div>
                </div>
                <button onClick={() => setWbs('')} className="text-[10px]"
                  style={{ color: C.slateMuted }}>change</button>
              </div>
            ) : (
              <input value={wbsSearch || ''} onChange={e => setWbsSearch(e.target.value)}
                onFocus={() => setWbsDropdownOpen(true)}
                onBlur={() => setTimeout(() => setWbsDropdownOpen(false), 200)}
                placeholder={wbsList.length ? `Type or browse ${wbsList.length} activities…` : 'Search by code / activity name / phase'}
                className={inp} style={{ borderColor: C.border }}
                data-testid="mir-wbs-search" />
            )}
            {!wbs && (wbsDropdownOpen || (wbsSearch && wbsSearch.length >= 1)) && (() => {
              const q = (wbsSearch || '').toLowerCase();
              const all = wbsList.filter(t =>
                !q ||
                ((t.wbsCode || t.activityId || t.id) || '').toLowerCase().includes(q)
                || (t.name || t.taskName || '').toLowerCase().includes(q)
                || (t.phase || t.parent || '').toLowerCase().includes(q)
              );
              const matches = all.slice(0, 40);
              if (matches.length === 0) return null;
              return (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded shadow-lg z-30 max-h-72 overflow-y-auto"
                  style={{ borderColor: C.border }}
                  data-testid="mir-wbs-dropdown">
                  {matches.map((t, i) => {
                    const code = t.wbsCode || t.activityId || t.id;
                    const depth = t.depth != null ? t.depth : (code || '').split('.').length - 1;
                    return (
                      <button key={i} onMouseDown={() => { setWbs(code); setWbsSearch(''); setWbsDropdownOpen(false); }}
                        className="block w-full text-left px-2 py-1 text-[10px] hover:bg-slate-50"
                        style={{ borderBottom: `1px solid ${C.border}`, paddingLeft: 8 + depth * 12 }}
                        data-testid={`mir-wbs-option-${i}`}>
                        <div className="font-mono font-bold" style={{ color: C.primary }}>{code}</div>
                        <div className="truncate" style={{ color: C.slate }}>{t.name || t.taskName}</div>
                        {(t.phase || t.parent) && (
                          <div style={{ color: C.slateMuted, fontSize: 8.5 }}>{t.phase || t.parent}</div>
                        )}
                      </button>
                    );
                  })}
                  {all.length > matches.length && (
                    <div className="px-2 py-1 text-[9px] italic border-t" style={{ color: C.slateMuted, borderColor: C.border }}>
                      +{all.length - matches.length} more · keep typing to narrow
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <div>
            <label className={lbl} style={{ color: C.slateMuted }}>Workfront</label>
            <input value={workfront} onChange={e => setWorkfront(e.target.value)}
              list="mir-workfront-list"
              placeholder="Piping Area-100" className={inp} style={{ borderColor: C.border }}
              data-testid="mir-workfront" />
            <datalist id="mir-workfront-list">
              {[...new Set(wbsList.map(t => t.workfront).filter(Boolean))].map(w => (
                <option key={w} value={w} />
              ))}
            </datalist>
          </div>
          <div className="col-span-2">
            <label className={lbl} style={{ color: C.slateMuted }}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              rows={2} className={inp} style={{ borderColor: C.border }}
              data-testid="mir-notes" />
          </div>
        </div>
        <div className="p-3 border-t flex items-center justify-between" style={{ borderColor: C.border }}>
          <div className="text-[9px]" style={{ color: C.slateMuted }}>
            Fields marked * are required · Requester and timestamp are captured automatically
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={saving}
              className="text-white font-semibold"
              style={{ background: C.accent }} data-testid="mir-submit">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
              Create MIR
            </Button>
          </div>
        </div>
      </div>
      {importOpen && <MirExcelDialog projectId={projectId}
        onClose={() => setImportOpen(false)}
        onImported={() => { setImportOpen(false); onCreated?.(); onClose(); }} />}
    </div>
  );
}

function MirExcelDialog({ projectId, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const doImport = async () => {
    if (!file) return toast.error('Pick an Excel file first');
    setBusy(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(',')[1] || '');
        r.onerror = rej; r.readAsDataURL(file);
      });
      const { data } = await api.post(`/v1/projects/${projectId}/mtx/mirs/import`,
        { xlsxBase64: b64 });
      setReport(data);
      if (data.created > 0) toast.success(`${data.created} MIR(s) created · ${data.errors?.length || 0} rejected`);
      else toast.warning(`No MIRs created · ${data.errors?.length || 0} errors`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose} data-testid="mir-excel-dialog">
      <div className="bg-white rounded-xl w-full max-w-xl shadow-xl p-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4" style={{ color: C.success }} />
            <div className="text-sm font-bold">Bulk MIR — Excel Import / Export</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <a href={`${backendBase}/api/v1/projects/${projectId}/mtx/mirs/template.xlsx`}
            className="p-2 rounded border text-center block hover:bg-slate-50"
            style={{ borderColor: C.border }} data-testid="mir-tpl-download">
            <div className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Step 1</div>
            <div className="text-[11px] font-bold" style={{ color: C.primary }}>Download Template</div>
            <div className="text-[9px]" style={{ color: C.slateMuted }}>Pre-filled with warehouse + MCI dropdowns</div>
          </a>
          <a href={`${backendBase}/api/v1/projects/${projectId}/mtx/mirs/export.xlsx`}
            className="p-2 rounded border text-center block hover:bg-slate-50"
            style={{ borderColor: C.border }} data-testid="mir-export">
            <div className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Also</div>
            <div className="text-[11px] font-bold" style={{ color: C.accent }}>Export Existing MIRs</div>
            <div className="text-[9px]" style={{ color: C.slateMuted }}>All open + closed MIRs to Excel</div>
          </a>
        </div>
        <div className="mb-2 text-[10px]" style={{ color: C.slateMuted }}>
          <b>Step 2</b>: Fill the template. Each row = one MIR line. Columns: warehouseLocation, mciLineNumber, qty, workfront, wbsActivityId, requiredByDate (YYYY-MM-DD), notes.
        </div>
        <input type="file" accept=".xlsx" onChange={e => setFile(e.target.files?.[0])}
          data-testid="mir-import-file" className="text-[10px] mb-2 block" />
        {report && (
          <div className="p-2 mb-2 rounded border text-[10px]" style={{ borderColor: C.border }}
            data-testid="mir-import-report">
            <div><b style={{ color: C.success }}>{report.created}</b> MIR(s) created,
                  <b style={{ color: C.danger }}> {report.errors?.length || 0}</b> row(s) rejected.</div>
            {(report.errors || []).slice(0, 10).map((e, i) => (
              <div key={i} className="text-[9px]" style={{ color: C.danger }}>
                Row {e.row}: {e.error}
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
          <Button size="sm" onClick={doImport} disabled={busy || !file}
            style={{ background: C.accent, color: '#fff' }}
            data-testid="mir-import-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Import'}
          </Button>
        </div>
        <div className="mt-2 text-[9px]" style={{ color: C.slateMuted }}>
          Server validates every row before creating any MIR — warehouse must exist, MCI must be active in that warehouse,
          Qty must be numeric and ≤ available. Errors are reported per-row without aborting the batch.
        </div>
      </div>
    </div>
  );
}

function MirCard({ mir, projectId, persona, onApprove, onStatus, onOpenMci, onWorkflowStep }) {
  const [printGp, setPrintGp] = useState(null);
  const [busyWf, setBusyWf] = useState(false);
  const nextStatus = {
    APPROVED: 'ISSUED', ISSUED: 'AT_WH_GATE', AT_WH_GATE: 'IN_TRANSIT',
    IN_TRANSIT: 'AT_SITE_GATE', AT_SITE_GATE: 'RECEIVED', RECEIVED: 'INSTALLED',
  };
  const next = nextStatus[mir.status];
  // The 4-tier workflow shows a role-specific action when the MIR's
  // currentApprovalStage matches the acting persona AND the MIR is still
  // pending (SUBMITTED / DRAFTED / AWAITING_STOCK).
  const stage = mir.currentApprovalStage || 'PACKAGE_MANAGER';
  const pendingStatus = ['SUBMITTED', 'DRAFTED', 'AWAITING_STOCK'].includes(mir.status);
  const canWorkflowApprove =
    pendingStatus && persona && persona.id !== 'SITE_ENGINEER' && stage === persona.id;
  const openPrint = async () => {
    if (!mir.gatePassId) return;
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/mtx/gate-passes/${mir.gatePassId}`);
      setPrintGp(data);
    } catch { toast.error('Gate Pass not found'); }
  };
  const doWf = async (decision) => {
    setBusyWf(true);
    try { await onWorkflowStep?.(mir.id, persona.id, decision); }
    finally { setBusyWf(false); }
  };
  return (
    <div className="p-2 rounded border mb-1.5 bg-white" style={{ borderColor: C.border }}
      data-testid={`mir-card-${mir.code}`}>
      <div className="flex items-center justify-between gap-1">
        <div className="text-[10px] font-mono font-bold" style={{ color: C.primary }}>{mir.code}</div>
        {mir.awaitingStock && (
          <span className="text-[8.5px] px-1 py-0.5 rounded font-bold" style={{ background: '#FEF3C7', color: '#92400E' }}
                title="No IQC-Accepted stock yet — will reserve as soon as it lands">
            ⏳ No stock
          </span>
        )}
      </div>
      {mir.currentApprovalStage && pendingStatus && (
        <div className="text-[8.5px] mt-0.5 px-1 rounded inline-block"
             style={{ background: '#EEF2FF', color: C.primary }}
             data-testid={`mir-card-stage-${mir.code}`}>
          Pending {String(mir.currentApprovalStage).replace(/_/g, ' ').toLowerCase()}
        </div>
      )}
      {(mir.lineItems || []).map((li, i) => (
        <button key={i} onClick={(e) => { e.stopPropagation(); onOpenMci?.(li.mciLineNumber); }}
          className="text-left text-[10px] mt-0.5 hover:underline block w-full"
          data-testid={`mir-card-mci-${mir.code}-${i}`}
          title="Open MCI · GRN · PO drilldown">
          <span style={{ color: C.slate }}>{li.mciLineNumber} · {li.qty}</span>
          <ExternalLink className="w-2.5 h-2.5 inline ml-1" style={{ color: C.slateMuted }} />
        </button>
      ))}
      <div className="text-[9px] mt-0.5" style={{ color: C.slateMuted }}>
        {mir.warehouseLocation} · {mir.workfront || 'no workfront'}
      </div>
      {mir.gatePassId && (
        <div className="mt-0.5 text-[9px] px-1 rounded inline-block" style={{ background: '#FEF3C7', color: '#92400E' }}
          data-testid={`mir-card-gp-${mir.code}`}>
          <Ticket className="w-2.5 h-2.5 inline mr-0.5" />
          GP: {mir.gatePassId}
        </div>
      )}
      {(mir.markAllocations || []).length > 0 && (
        <div className="mt-1 px-1 py-0.5 rounded" style={{ background: '#FAF5FF', border: '1px solid #E9D5FF' }}
          data-testid={`mir-card-marks-${mir.code}`}>
          <div className="text-[8.5px] uppercase font-bold" style={{ color: '#7C3AED' }}>
            🔒 {mir.markAllocations.length} mark{mir.markAllocations.length > 1 ? 's' : ''} · per-mark ledger split
          </div>
          <div className="text-[8.5px] mt-0.5 flex flex-wrap gap-0.5">
            {mir.markAllocations.slice(0, 4).map((ma, i) => (
              <span key={i} className="px-1 rounded font-mono" style={{ background: '#F3E8FF', color: '#6B21A8' }}
                title={`${ma.structuralTag} → ${ma.markNumber}`}>
                {(ma.markNumber || '').split('/').pop()}·{ma.issueQty}
              </span>
            ))}
            {mir.markAllocations.length > 4 && (
              <span className="px-1 rounded" style={{ background: '#F3E8FF', color: '#6B21A8' }}>+{mir.markAllocations.length - 4}</span>
            )}
          </div>
        </div>
      )}
      <div className="flex gap-1 mt-1.5 flex-wrap">
        {canWorkflowApprove && (
          <>
            <button onClick={() => doWf('APPROVE')} disabled={busyWf}
              className="text-[9px] px-2 py-0.5 rounded text-white font-semibold disabled:opacity-50"
              style={{ background: persona.color }}
              title={`Approve as ${persona.label} · captures GPS`}
              data-testid={`mir-wf-approve-${mir.code}`}>
              {busyWf ? <Loader2 className="w-2.5 h-2.5 animate-spin inline mr-0.5"/> : <CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5"/>}
              Approve as {persona.label}
            </button>
            <button onClick={() => doWf('REJECT')} disabled={busyWf}
              className="text-[9px] px-2 py-0.5 rounded font-semibold disabled:opacity-50"
              style={{ background: '#FEE2E2', color: C.danger, border: '1px solid #FCA5A5' }}
              data-testid={`mir-wf-reject-${mir.code}`}>
              <XCircle className="w-2.5 h-2.5 inline mr-0.5"/> Reject
            </button>
          </>
        )}
        {!canWorkflowApprove && mir.status === 'SUBMITTED' && persona?.id === 'STORE_INCHARGE' && (
          <button onClick={() => onApprove(mir.id)}
            className="text-[9px] px-2 py-0.5 rounded text-white font-semibold"
            style={{ background: C.success }}
            data-testid={`mir-approve-${mir.code}`}>
            <Check className="w-2.5 h-2.5 inline mr-0.5" /> Dispatch (auto-reserve + Gate Pass)
          </button>
        )}
        {mir.gatePassId && (
          <button onClick={openPrint}
            className="text-[9px] px-2 py-0.5 rounded font-semibold"
            style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D' }}
            data-testid={`mir-print-gp-${mir.code}`}
            title="Print A5 Gate Pass label (QR + RFID barcodes)">
            <Printer className="w-2.5 h-2.5 inline mr-0.5" /> Print GP
          </button>
        )}
        {next && (
          <button onClick={() => onStatus(mir.id, next)}
            className="text-[9px] px-2 py-0.5 rounded"
            style={{ background: '#EEF2FF', color: C.primary }}
            data-testid={`mir-advance-${mir.code}`}
            title={`Move to ${next.replace(/_/g, ' ')}`}>
            <ChevronRight className="w-2.5 h-2.5 inline" /> {next.replace(/_/g, ' ')}
          </button>
        )}
      </div>
      {printGp && <GatePassLabel projectId={projectId} gatePass={printGp} mir={mir} onClose={() => setPrintGp(null)} />}
    </div>
  );
}

function GatePassRegister({ projectId, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printGp, setPrintGp] = useState(null);
  const [drillGp, setDrillGp] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mtx/field-issue/gate-passes`);
        if (alive) setRows(data.gatePasses || []);
      } catch { /* silent */ }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [projectId, refreshKey]);
  return (
    <div className="rounded-lg border" style={{ borderColor: C.border }}
      data-testid="gate-pass-register">
      <div className="px-2 py-1.5 border-b flex items-center gap-1.5" style={{ borderColor: C.border }}>
        <Ticket className="w-3.5 h-3.5" style={{ color: C.accent }} />
        <div className="text-[10px] uppercase font-bold" style={{ color: C.accent }}>Gate Pass Register</div>
        <span className="ml-auto text-[10px] font-mono px-1 rounded" style={{ background: '#FEF3C7', color: '#92400E' }}>{rows.length} active</span>
      </div>
      {loading && <div className="p-3 text-center text-[10px]" style={{ color: C.slateMuted }}>Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="p-3 text-center text-[10px]" style={{ color: C.slateMuted }}>
          No active gate passes. Approve a MIR to auto-generate one.
        </div>
      )}
      {!loading && rows.length > 0 && (
        <table className="w-full text-[11px]">
          <thead style={{ background: '#F8FAFC' }}>
            <tr>
              {['GP Code', 'MIR', 'Workfront', 'Warehouse', 'Units', 'RFID Tags', 'Issued At', 'TTL', 'Status', ''].map(h => (
                <th key={h} className="text-left p-1.5 uppercase text-[9px] font-semibold" style={{ color: C.slateMuted }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(gp => (
              <tr key={gp.id} style={{ borderTop: `1px solid ${C.border}`, cursor: 'pointer' }}
                className="hover:bg-slate-50"
                onClick={() => setDrillGp(gp)}
                data-testid={`gp-row-${gp.code}`}>
                <td className="p-1.5 font-mono text-[10px] font-bold" style={{ color: C.accent }}>{gp.code}</td>
                <td className="p-1.5 font-mono text-[10px]" style={{ color: C.primary }}>{gp.mir?.code || gp.mirId}</td>
                <td className="p-1.5 text-[10px]" style={{ color: C.slate }}>{gp.mir?.workfront || '—'}</td>
                <td className="p-1.5 text-[10px]" style={{ color: C.slateMuted }}>{gp.mir?.warehouseLocation || '—'}</td>
                <td className="p-1.5 text-[10px] font-mono" style={{ color: C.slate }}>{gp.unitCount || 0}</td>
                <td className="p-1.5 text-[10px] font-mono" style={{ color: C.slateMuted }}>{(gp.rfidTagList || []).length || (gp.unitCount || 0)}</td>
                <td className="p-1.5 text-[10px]" style={{ color: C.slateMuted }}>{(gp.issuedAt || '').slice(0, 16).replace('T', ' ')}</td>
                <td className="p-1.5 text-[10px]" style={{ color: C.slateMuted }}>{gp.ttlBetweenScansMinutes}min</td>
                <td className="p-1.5"><span className="px-1 rounded text-[9px] font-bold text-white" style={{ background: C.success }}>{gp.status}</span></td>
                <td className="p-1.5">
                  <button onClick={(e) => { e.stopPropagation(); setPrintGp(gp); }}
                    data-testid={`gp-print-${gp.code}`}
                    className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                    style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D' }}
                    title="Print A5 Gate Pass label (QR + RFID barcodes)">
                    <Printer className="w-2.5 h-2.5 inline mr-0.5" /> Print
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {printGp && <GatePassLabel projectId={projectId} gatePass={printGp} mir={printGp.mir} onClose={() => setPrintGp(null)} />}
      {drillGp && <GatePassDrillDrawer projectId={projectId} gatePass={drillGp} onClose={() => setDrillGp(null)} />}
    </div>
  );
}

function GatePassDrillDrawer({ projectId, gatePass, onClose }) {
  const [units, setUnits] = useState([]);
  const [scans, setScans] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const uidList = gatePass.materialUnitIds || [];
        if (uidList.length === 0) return;
        // Load units + last scan events for each
        const { data: u } = await api.get(
          `/v1/projects/${projectId}/mtx/material-units?ids=${uidList.slice(0, 20).join(',')}`);
        setUnits(u.units || u.materialUnits || []);
        // Aggregate scans across all units on this GP
        const all = [];
        for (const uid of uidList.slice(0, 5)) {
          try {
            const { data } = await api.get(
              `/v1/projects/${projectId}/mtx/field-tracking/chain-of-custody/${uid}`);
            (data.events || []).forEach(ev => all.push({ ...ev, unitId: uid }));
          } catch { /* skip */ }
        }
        all.sort((a, b) => (a.scannedAt || '').localeCompare(b.scannedAt || ''));
        setScans(all);
      } catch { /* silent */ }
    })();
  }, [projectId, gatePass]);
  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" style={{ background: 'rgba(15,23,42,0.4)' }} onClick={onClose}>
      <div className="w-[520px] bg-white shadow-2xl overflow-y-auto p-4" onClick={e => e.stopPropagation()}
        data-testid="gp-drill-drawer">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Ticket className="w-4 h-4" style={{ color: C.accent }} />
              <div className="text-sm font-bold" style={{ color: C.slate }}>Gate Pass {gatePass.code}</div>
            </div>
            <div className="text-[10px]" style={{ color: C.slateMuted }}>
              MIR {gatePass.mir?.code || gatePass.mirId} · {gatePass.unitCount || 0} units · TTL {gatePass.ttlBetweenScansMinutes}min · <b>{gatePass.status}</b>
            </div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="p-2 rounded border" style={{ borderColor: C.border }}>
            <div className="text-[9px] uppercase" style={{ color: C.slateMuted }}>Warehouse</div>
            <div className="text-[11px] font-bold">{gatePass.mir?.warehouseLocation || '—'}</div>
          </div>
          <div className="p-2 rounded border" style={{ borderColor: C.border }}>
            <div className="text-[9px] uppercase" style={{ color: C.slateMuted }}>Workfront</div>
            <div className="text-[11px] font-bold">{gatePass.mir?.workfront || '—'}</div>
          </div>
          <div className="p-2 rounded border" style={{ borderColor: C.border }}>
            <div className="text-[9px] uppercase" style={{ color: C.slateMuted }}>Issued At</div>
            <div className="text-[11px]">{(gatePass.issuedAt || '').slice(0, 16).replace('T', ' ')}</div>
          </div>
          <div className="p-2 rounded border" style={{ borderColor: C.border }}>
            <div className="text-[9px] uppercase" style={{ color: C.slateMuted }}>RFID Tags</div>
            <div className="text-[11px] font-mono">{(gatePass.rfidTagList || []).length}</div>
          </div>
        </div>
        <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>Material Units on Pass</div>
        <div className="rounded border max-h-40 overflow-y-auto mb-3" style={{ borderColor: C.border }}>
          {units.slice(0, 20).map((u, i) => (
            <div key={i} className="p-1.5 text-[10px] border-b" style={{ borderColor: C.border }}>
              <div className="flex justify-between">
                <span className="font-mono font-bold">{u.id}</span>
                <span style={{ color: C.slateMuted }}>{u.currentState}</span>
              </div>
              <div className="truncate" style={{ color: C.slateMuted }}>{u.materialDescription || u.mciLineNumber}</div>
              {u.rfidTag && <div className="text-[9px] font-mono" style={{ color: C.accent }}>RFID: {u.rfidTag}</div>}
            </div>
          ))}
          {units.length === 0 && (
            <div className="p-2 text-[10px] italic text-center" style={{ color: C.slateMuted }}>No units resolved</div>
          )}
        </div>
        <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>Scan Timeline ({scans.length})</div>
        <div className="rounded border max-h-56 overflow-y-auto" style={{ borderColor: C.border }}>
          {scans.map((s, i) => (
            <div key={i} className="p-1.5 text-[9.5px] border-b" style={{ borderColor: C.border }}>
              <span className="font-mono font-bold" style={{ color: C.primary }}>{s.interfacePointType}</span>
              {' · '}
              <span style={{ color: C.slate }}>{s.previousState} → {s.newState}</span>
              <div style={{ color: C.slateMuted }}>{s.scannedBy} · {new Date(s.scannedAt).toLocaleString()}</div>
            </div>
          ))}
          {scans.length === 0 && (
            <div className="p-2 text-[10px] italic text-center" style={{ color: C.slateMuted }}>No scans recorded yet</div>
          )}
        </div>
      </div>
    </div>
  );
}

function InterfaceStatusWidget({ projectId, refreshKey }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drillIp, setDrillIp] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mtx/field-issue/interface-status`);
        if (alive) setPoints(data.points || []);
      } catch { /* silent */ }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [projectId, refreshKey]);

  const color = (s) => s === 'ONLINE' ? C.success : s === 'MISSING' ? C.danger : C.warn;

  return (
    <div className="rounded-lg border" style={{ borderColor: C.border }}
      data-testid="interface-status-widget">
      <div className="px-2 py-1.5 border-b flex items-center gap-1.5" style={{ borderColor: C.border }}>
        <Radio className="w-3.5 h-3.5" style={{ color: C.primary }} />
        <div className="text-[10px] uppercase font-bold" style={{ color: C.primary }}>Interface Point Status (12)</div>
        <span className="ml-auto text-[10px] font-mono" style={{ color: C.slateMuted }}>
          {points.filter(p => p.status === 'ONLINE').length} / 12 online
        </span>
      </div>
      {loading && <div className="p-3 text-center text-[10px]" style={{ color: C.slateMuted }}>Loading…</div>}
      {!loading && (
        <div className="grid grid-cols-4 gap-1 p-2">
          {points.map((p, i) => (
            <div key={p.type} className="p-1.5 rounded border text-[9px] cursor-pointer hover:shadow"
              style={{ borderColor: C.border, background: p.status === 'MISSING' ? '#FEF2F2' : '#F8FAFC' }}
              onClick={() => setDrillIp(p)}
              data-testid={`ip-status-${p.type}`}>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: color(p.status) }}></div>
                <span className="font-semibold" style={{ color: C.slate }}>#{i + 1}</span>
              </div>
              <div className="text-[9px] mt-0.5 truncate" style={{ color: C.slateMuted }}>{p.type.replace(/_/g, ' ')}</div>
              <div className="text-[8.5px] mt-0.5 font-mono" style={{ color: C.primary }}>
                {p.totalScans || 0} scans · {p.scans24h || 0} in 24h
              </div>
            </div>
          ))}
        </div>
      )}
      {drillIp && <IpDrillDrawer projectId={projectId} ip={drillIp} onClose={() => setDrillIp(null)} />}
    </div>
  );
}

function IpDrillDrawer({ projectId, ip, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/v1/projects/${projectId}/mtx/interface-points/${ip.id}/recent-scans?limit=30`);
        setD(data);
      } catch { setD({ events: [], interfacePoint: ip }); }
    })();
  }, [projectId, ip]);
  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" style={{ background: 'rgba(15,23,42,0.4)' }} onClick={onClose}>
      <div className="w-[440px] bg-white shadow-2xl overflow-y-auto p-4" onClick={e => e.stopPropagation()}
        data-testid="ip-drill-drawer">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4" style={{ color: C.primary }} />
              <div className="text-sm font-bold" style={{ color: C.slate }}>{ip.type.replace(/_/g, ' ')}</div>
            </div>
            <div className="text-[10px]" style={{ color: C.slateMuted }}>
              Status: <b style={{ color: ip.status === 'ONLINE' ? C.success : C.danger }}>{ip.status}</b>
              {' · '}Scans total: <b>{ip.totalScans || 0}</b> (24h: <b>{ip.scans24h || 0}</b>)
              {' · '}Last ping: {ip.lastPingAt ? new Date(ip.lastPingAt).toLocaleString() : '—'}
            </div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>
          Recent Scans ({(d?.events || []).length})
        </div>
        <div className="rounded border overflow-y-auto" style={{ borderColor: C.border, maxHeight: 480 }}>
          {(d?.events || []).map((e, i) => (
            <div key={i} className="p-1.5 text-[10px] border-b" style={{ borderColor: C.border }}>
              <div className="flex justify-between">
                <span className="font-mono font-bold" style={{ color: C.primary }}>{e.unit?.materialCode || e.materialUnitId?.slice(-8)}</span>
                <span style={{ color: C.slateMuted }}>{new Date(e.scannedAt).toLocaleString()}</span>
              </div>
              <div className="truncate" style={{ color: C.slateMuted }}>{e.unit?.materialDescription}</div>
              <div className="text-[9px]" style={{ color: C.accent }}>{e.previousState} → {e.newState} · {e.scannedBy}</div>
            </div>
          ))}
          {d && (d.events || []).length === 0 && (
            <div className="p-3 text-[10px] italic text-center" style={{ color: C.slateMuted }}>No scans yet on this interface point</div>
          )}
        </div>
      </div>
    </div>
  );
}

function CognaraPanel({ projectId, refreshKey }) {
  const [ins, setIns] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mtx/field-issue/cognara-insights`);
        if (alive) setIns(data.insights || []);
      } catch { /* silent */ }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [projectId, refreshKey]);
  const sevColor = { HIGH: C.danger, MEDIUM: C.warn, INFO: C.info };
  return (
    <div className="sticky top-4 w-64 flex-shrink-0 rounded-lg border p-2"
      style={{ borderColor: C.border, background: 'linear-gradient(180deg, #FEF3C7 0%, #FFFFFF 100%)' }}
      data-testid="cognara-panel">
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="w-3.5 h-3.5" style={{ color: C.accent }} />
        <div className="text-[10px] uppercase font-bold" style={{ color: C.accent }}>Cognara · MIR Flow</div>
      </div>
      {loading && <div className="text-[10px] text-center py-3" style={{ color: C.slateMuted }}>Analyzing…</div>}
      {!loading && ins.map((i, idx) => (
        <div key={idx} className="mb-2 p-2 rounded border bg-white" style={{ borderColor: C.border }}
          data-testid={`cognara-insight-${idx}`}>
          <div className="flex items-center gap-1 mb-1">
            <span className="px-1 rounded text-[8.5px] font-bold text-white" style={{ background: sevColor[i.severity] || C.slateMuted }}>{i.severity}</span>
            <div className="text-[10px] font-bold" style={{ color: C.slate }}>{i.title}</div>
          </div>
          <div className="text-[9px]" style={{ color: C.slateMuted }}>{i.detail}</div>
          <div className="text-[9px] mt-1 italic" style={{ color: C.primary }}>→ {i.action}</div>
        </div>
      ))}
    </div>
  );
}

export default function FieldIssueTab({ projectId, prefill, onPrefillConsumed }) {
  const [data, setData] = useState({ buckets: {}, mirs: [] });
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(!!prefill);
  const [drillMci, setDrillMci] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [autoPrintGp, setAutoPrintGp] = useState(null);   // { gatePass, mir }
  const [personaId, setPersonaId] = useState('SITE_ENGINEER');
  const [showHelp, setShowHelp] = useState(true);
  const persona = useMemo(() => PERSONAS.find(p => p.id === personaId) || PERSONAS[0], [personaId]);

  // Current-user helper for filtering Site Engineer's own MIRs
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('ciq_user') || '{}'); }
    catch { return {}; }
  }, []);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [m, k] = await Promise.all([
        api.get(`/v1/projects/${projectId}/mtx/mirs`),
        api.get(`/v1/projects/${projectId}/mtx/field-issue/kpis`),
      ]);
      setData(m.data); setKpis(k.data);
    } catch { /* silent */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      const { data: r } = await api.patch(`/v1/projects/${projectId}/mtx/mirs/${id}/approve`);
      toast.success(`Approved · Gate Pass ${r.gatePass.code} · ${r.reservedUnits} units reserved (FIFO by GRN)`);
      load(); setRefreshKey(k => k + 1);
      // Auto-print the freshly generated A5 Gate Pass label so security has
      // the physical pass in hand before the material leaves the warehouse.
      const mir = (data.mirs || []).find(m => m.id === id) || { code: r.mirId, id };
      setAutoPrintGp({ gatePass: r.gatePass, mir: { ...mir, code: mir.code || r.mirId } });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
  };

  // 4-tier workflow — records a role decision with GPS + notes and, on the
  // last approver (STORE_INCHARGE), auto-fires the FIFO-reserve + Gate Pass.
  const workflowStep = async (mirId, roleId, decision) => {
    try {
      const gps = await captureGps().catch(() => null);
      const actor = {
        userId: currentUser?._id || currentUser?.id,
        email:  currentUser?.email,
        name:   currentUser?.fullName || currentUser?.name || currentUser?.email,
        role:   roleId,
      };
      const { data: r } = await api.post(
        `/v1/projects/${projectId}/mtx/mirs/${mirId}/workflow-approve`,
        { role: roleId, decision, gps, actor });
      if (decision === 'REJECT') {
        toast.warning(`MIR rejected by ${PERSONAS.find(p => p.id === roleId)?.label}`);
      } else if (r.dispatched) {
        toast.success(`Dispatched · Gate Pass ${r.gatePass?.code || ''} · ${r.reservedUnits || 0} units reserved`);
        if (r.gatePass) {
          const mir = (data.mirs || []).find(m => m.id === mirId) || { id: mirId, code: mirId };
          setAutoPrintGp({ gatePass: r.gatePass, mir });
        }
      } else if (r.warning) {
        toast.warning(`Approved by Store Incharge but ${r.warning}`);
      } else {
        toast.success(`Approved as ${PERSONAS.find(p => p.id === roleId)?.label} → next: ${(r.nextStage || '').replace(/_/g, ' ')}`);
      }
      load(); setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Workflow step failed');
    }
  };

  const advance = async (id, status) => {
    try {
      await api.patch(`/v1/projects/${projectId}/mtx/mirs/${id}/status`, { status });
      toast.success(`Moved to ${status.replace(/_/g, ' ')}`);
      load(); setRefreshKey(k => k + 1);
    } catch { toast.error('Status update failed'); }
  };

  const bootstrap = async () => {
    try {
      const [ip, mu] = await Promise.all([
        api.post(`/v1/projects/${projectId}/mtx/bootstrap`),
        api.post(`/v1/projects/${projectId}/mtx/material-units/sync-from-iqc`),
      ]);
      toast.success(`Bootstrapped ${ip.data.totalPoints} interface points · minted ${mu.data.mintedUnits} units from IQC`);
      load(); setRefreshKey(k => k + 1);
    } catch { toast.error('Bootstrap failed'); }
  };

  // Per-persona Kanban filter — only render lanes relevant to the acting
  // role, and within each lane, filter cards by ownership / stage where
  // it makes sense (Site Engineer sees only their own MIRs; approvers see
  // MIRs whose currentApprovalStage matches their role).
  const filteredBuckets = useMemo(() => {
    const out = {};
    const src = data.buckets || {};
    const lanes = persona.stageColumns.length ? persona.stageColumns : BUCKETS.map(b => b.key);
    lanes.forEach((laneKey) => {
      const rows = (src[laneKey] || []).filter((m) => {
        if (persona.id === 'SITE_ENGINEER') {
          const owner = m.requestedBy?.email || m.requestedBy?.userId;
          if (!owner || !currentUser?.email) return true;
          return owner === currentUser.email;
        }
        if (['PACKAGE_MANAGER', 'PROJECT_MANAGER'].includes(persona.id)) {
          // Approver only sees MIRs currently pending at their stage.
          if (!['SUBMITTED', 'DRAFTED', 'AWAITING_STOCK'].includes(m.status)) return false;
          const stage = m.currentApprovalStage || 'PACKAGE_MANAGER';
          return stage === persona.id;
        }
        if (persona.id === 'STORE_INCHARGE') {
          // Store sees anything ready for dispatch or already dispatched (transit stages).
          if (['SUBMITTED', 'DRAFTED', 'AWAITING_STOCK'].includes(m.status)) {
            return (m.currentApprovalStage || '') === 'STORE_INCHARGE';
          }
          return true;
        }
        return true;
      });
      out[laneKey] = rows;
    });
    return out;
  }, [data.buckets, persona, currentUser.email]);

  const visibleLanes = useMemo(
    () => BUCKETS.filter(b => (persona.stageColumns || []).includes(b.key)),
    [persona]);

  return (
    <div className="p-3 flex gap-3" data-testid="field-issue-tab">
      <div className="flex-1 min-w-0 space-y-3">
        {/* Persona strip + role chain */}
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: C.border, background: '#fff' }}
             data-testid="fi-persona-strip">
          <div className="px-3 py-2 border-b flex items-center gap-2 flex-wrap"
               style={{ borderColor: C.border, background: '#F8FAFC' }}>
            <div>
              <div className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Acting as</div>
              <div className="text-xs font-bold flex items-center gap-1" style={{ color: persona.color }}>
                <persona.icon className="w-3.5 h-3.5"/> {persona.label}
              </div>
            </div>
            <div className="mx-3 h-8 w-px" style={{ background: C.border }}/>
            <div className="flex items-center gap-1 flex-wrap flex-1">
              {PERSONAS.map((p, i) => {
                const Icon = p.icon;
                const active = p.id === personaId;
                return (
                  <span key={p.id} className="inline-flex items-center gap-1">
                    <button onClick={() => setPersonaId(p.id)}
                      data-testid={`fi-persona-${p.id.toLowerCase()}`}
                      className="text-[10px] px-2 py-1 rounded font-semibold inline-flex items-center gap-1 transition-all"
                      style={{
                        background: active ? p.color : '#fff',
                        color: active ? '#fff' : C.slate,
                        border: active ? `1px solid ${p.color}` : `1px solid ${C.border}`,
                      }}
                      title={p.help}>
                      <Icon className="w-3 h-3"/> {i + 1}. {p.label}
                    </button>
                    {i < PERSONAS.length - 1 && <ArrowRight className="w-2.5 h-2.5" style={{ color: C.slateMuted }}/>}
                  </span>
                );
              })}
            </div>
            <button onClick={() => setShowHelp(h => !h)}
                    className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded"
                    style={{ background: '#EEF2FF', color: C.primary, border: `1px solid ${C.border}` }}
                    data-testid="fi-persona-help-toggle">
              <HelpCircle className="w-3 h-3"/> {showHelp ? 'Hide' : 'Show'} help
            </button>
          </div>
          {showHelp && (
            <div className="px-3 py-2 flex items-start gap-2"
                 style={{ background: `${persona.color}0F`, color: C.slate }}
                 data-testid="fi-persona-help">
              <persona.icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: persona.color }}/>
              <div className="text-[11px] leading-snug">
                <b style={{ color: persona.color }}>{persona.label} · {persona.actionLabel}: </b>
                {persona.help}
              </div>
            </div>
          )}
        </div>

        {/* KPI band — 6 tiles per spec */}
        <div className="grid grid-cols-6 gap-2">
          {[
            { lbl: 'Open MIRs',            val: kpis.openMirs || 0,            color: C.primary, id: 'fi-kpi-open' },
            { lbl: 'Awaiting Approval',    val: kpis.awaitingApproval || 0,     color: C.warn,    id: 'fi-kpi-waiting' },
            { lbl: 'At WH Gate',           val: kpis.atWhGate || 0,            color: C.accent,  id: 'fi-kpi-whgate' },
            { lbl: 'In-Transit',           val: kpis.inTransit || 0,           color: '#0EA5E9', id: 'fi-kpi-transit' },
            { lbl: 'Received @ POU',       val: kpis.receivedAtPou || 0,       color: C.success, id: 'fi-kpi-received' },
            { lbl: 'Overdue Install >48h', val: kpis.overdueInstallations || 0, color: C.danger, id: 'fi-kpi-overdue' },
          ].map(k => (
            <div key={k.id} className="p-2 rounded border bg-white" style={{ borderColor: C.border }}
              data-testid={k.id}>
              <div className="text-[9px] uppercase" style={{ color: C.slateMuted }}>{k.lbl}</div>
              <div className="text-lg font-bold font-mono" style={{ color: k.color }}>{k.val}</div>
            </div>
          ))}
        </div>

        {/* Toolbar — persona-scoped actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {persona.id === 'SITE_ENGINEER' && (
            <Button size="sm" onClick={() => setShowCreate(true)}
              className="text-white font-semibold" style={{ background: C.accent }}
              data-testid="mir-create-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> New MIR
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={bootstrap} data-testid="mir-bootstrap-btn"
            title="First-run: seeds the 12 canonical Interface Points and mints MaterialUnits from every IQC-Accepted receipt already on this project">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Bootstrap MTX
          </Button>
          <Button size="sm" onClick={() => setScanOpen(true)}
            className="text-white font-semibold" style={{ background: C.primary }}
            data-testid="fi-scan-center-btn"
            title="Paste QR / RFID / MaterialUnit-ID to drive a state transition">
            <ScanLine className="w-3.5 h-3.5 mr-1" /> Scan Center
          </Button>
          <Button size="sm" variant="outline"
                  onClick={async () => {
                    try {
                      const { data: r } = await api.post(`/v1/projects/${projectId}/mtx/mirs/seed-workflow-demo`);
                      toast.success(`Seeded ${r.created} demo MIRs across all 4 roles`);
                      load(); setRefreshKey(k => k + 1);
                    } catch (e) { toast.error(e?.response?.data?.detail || 'Seed failed'); }
                  }}
                  title="Seed demo MIRs at every stage of the 4-tier workflow so all persona inboxes have real data"
                  data-testid="fi-seed-workflow-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Seed Demo Workflow
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { load(); setRefreshKey(k => k + 1); }}
            data-testid="fi-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <div className="text-[10px]" style={{ color: C.slateMuted }}>
            {persona.id === 'STORE_INCHARGE'
              ? 'Approving reserves stock FIFO by GRN date · TTL enforced: WH→Site 4h · Site→POU 8h · Install blocked without POU'
              : `${persona.label} view · showing ${visibleLanes.length} lane${visibleLanes.length === 1 ? '' : 's'} relevant to your role`}
          </div>
        </div>

        {loading && <div className="p-6 text-center text-sm" style={{ color: C.slateMuted }}>Loading MIRs…</div>}

        {/* Kanban — filtered by persona */}
        {!loading && (
          <div className="grid gap-1.5"
               style={{ minHeight: 240, gridTemplateColumns: `repeat(${Math.max(visibleLanes.length, 1)}, minmax(0, 1fr))` }}
               data-testid="mir-kanban">
            {visibleLanes.map(b => {
              const items = filteredBuckets?.[b.key] || [];
              return (
                <div key={b.key} className="rounded border" style={{ borderColor: C.border, background: '#FAFAFA' }}
                  data-testid={`mir-lane-${b.key}`}>
                  <div className="px-1.5 py-1 border-b flex items-center justify-between" style={{ borderColor: C.border }}>
                    <div className="text-[9px] uppercase font-bold truncate" style={{ color: b.color }}>{b.label}</div>
                    <div className="flex items-center gap-1">
                      {b.key === 'SUBMITTED' && persona.id === 'STORE_INCHARGE' && items.length > 0 && (
                        <button onClick={async () => {
                            if (!window.confirm(`Bulk-approve ${items.length} MIR(s)? Each approval reserves stock FIFO by GRN and auto-generates a Gate Pass.`)) return;
                            try {
                              const { data: r } = await api.post(`/v1/projects/${projectId}/mtx/mirs/bulk-approve`, { mirIds: items.map(m => m.id) });
                              toast.success(`Bulk approved ${r.approved}/${r.total} MIRs · Gate Passes auto-printed`);
                              load(); setRefreshKey(k => k + 1);
                            } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk approve failed'); }
                          }}
                          className="text-[8.5px] px-1.5 py-0.5 rounded font-bold text-white"
                          style={{ background: '#10B981' }}
                          title={`Approve all ${items.length} SUBMITTED MIRs at once`}
                          data-testid="mir-bulk-approve">
                          ⚡ Bulk {items.length}
                        </button>
                      )}
                      <span className="text-[9px] font-mono px-1 rounded" style={{ background: `${b.color}22`, color: b.color }}>{items.length}</span>
                    </div>
                  </div>
                  <div className="p-1 max-h-96 overflow-y-auto">
                    {items.length === 0 && <div className="text-[9px] text-center py-3" style={{ color: C.slateMuted }}>—</div>}
                    {items.map(m => <MirCard key={m.id} mir={m} projectId={projectId}
                                              persona={persona}
                                              onApprove={approve} onStatus={advance}
                                              onOpenMci={setDrillMci}
                                              onWorkflowStep={workflowStep} />)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Gate Pass Register */}
        <GatePassRegister projectId={projectId} refreshKey={refreshKey} />

        {/* Interface Point Status widget */}
        <InterfaceStatusWidget projectId={projectId} refreshKey={refreshKey} />

        {/* PWA Mobile scan note */}
        <div className="p-2 rounded border text-[10px]" style={{ borderColor: C.border, background: '#D1FAE5', color: '#065F46' }}>
          <Check className="w-3 h-3 inline mr-1" />
          <b>Mobile PWA is live</b> — open <a className="underline font-bold" target="_blank" rel="noreferrer" href={`/mobile/scan?project=${projectId}`}>Mobile Scan</a>
          (camera QR + BT/Serial RFID + offline IndexedDB queue).
        </div>
      </div>

      <CognaraPanel projectId={projectId} refreshKey={refreshKey} />

      {showCreate && <CreateMirDialog projectId={projectId} prefill={prefill}
        onClose={() => { setShowCreate(false); onPrefillConsumed?.(); }}
        onCreated={() => { load(); setRefreshKey(k => k + 1); onPrefillConsumed?.(); }} />}
      {scanOpen && <ScanCenterDialog projectId={projectId}
        onClose={() => setScanOpen(false)}
        onScanned={() => { load(); setRefreshKey(k => k + 1); }} />}
      {drillMci && <MciPackageDrawer projectId={projectId} mci={drillMci}
        onClose={() => setDrillMci(null)} />}
      {autoPrintGp && <GatePassLabel projectId={projectId}
        gatePass={autoPrintGp.gatePass} mir={autoPrintGp.mir}
        onClose={() => setAutoPrintGp(null)} />}
    </div>
  );
}
